/**
 * FileCard — File attachment chip.
 *
 * Compact pill style matching the pre-send chips.
 * Used in UserMessage (sent files) and ChatContainer (render helper).
 */

import React, { useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, Braces } from "lucide-react";
import { getFileExtension } from "../utils/messageUtils";

const IMAGE_EXTS = ["PNG", "JPG", "JPEG", "GIF", "WEBP", "SVG"];

const FileCard = ({
  file,
  fileMetadata = null,
  allowMetadata = false,
  onMetadataClick,
  secondaryColor,
}) => {
  const { t } = useTranslation();

  const isServerAttachment = file.original_file_name !== undefined;
  const fileName = isServerAttachment ? file.original_file_name : file.name;
  const fileType = isServerAttachment ? file.content_type : file.type;
  const actualMetadata =
    fileMetadata !== null
      ? fileMetadata
      : isServerAttachment
        ? file.metadata
        : null;
  const ext = getFileExtension(fileName);
  const isImage =
    (fileType && fileType.startsWith("image/")) || IMAGE_EXTS.includes(ext);

  const objectUrlRef = useRef(null);

  const previewUrl = useMemo(() => {
    try {
      if (isImage && !isServerAttachment) {
        const url = URL.createObjectURL(file);
        objectUrlRef.current = url;
        return url;
      }
      if (isImage && isServerAttachment && file.file_content) {
        return `data:${fileType};base64,${file.file_content}`;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [file, isImage, isServerAttachment, fileType]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, [previewUrl]);

  const hasMetadata =
    actualMetadata &&
    typeof actualMetadata === "object" &&
    Object.keys(actualMetadata).length > 0;

  const openFile = () => {
    try {
      if (isServerAttachment && file.file_content) {
        window.open(`data:${fileType};base64,${file.file_content}`, "_blank");
      } else if (file instanceof Blob) {
        // Always create a fresh URL so it can't be revoked before the tab loads
        const url = URL.createObjectURL(file);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else if (previewUrl) {
        window.open(previewUrl, "_blank");
      }
    } catch (e) {
      console.error("Cannot open file preview", e);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={openFile}
        className="inline-flex items-center gap-1.5 py-1 pl-2.5 pr-2.5 text-xs border rounded-lg bg-white/80 text-gray-600 max-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
        style={{ borderColor: secondaryColor || "#e5e7eb" }}
        title={fileName}
      >
        <Paperclip size={13} className="text-gray-400 shrink-0" />
        <span className="truncate">{fileName}</span>
      </button>
      {hasMetadata && allowMetadata && onMetadataClick && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onMetadataClick(fileName, actualMetadata);
          }}
          className="inline-flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
          title={t("viewMetadata")}
        >
          <Braces size={13} />
        </button>
      )}
    </span>
  );
};

export { FileCard };
